// frontend/src/pages/asset-admin/AssetAdminFleetAccidents.js
/**
 * Asset Admin — Fleet Vehicle Accidents
 *
 * Custodian working queue ordered by the insurer notification deadline.
 * Per-vehicle history lives in AssetDetailModal → Accidents.
 *
 * Data: GET /asset-inventory/vehicle-accidents/ (+ /summary/)
 * Theme: Red (Admin).
 */

import React, { useState } from 'react';
import {
  Siren, AlertTriangle, CalendarClock, FileText, CircleDollarSign, Plus,
  ChevronRight, AlertCircle, Inbox,
} from 'lucide-react';
import PillToggle from '../../components/UI/PillToggle';
import FormSelect from '../../components/UI/FormSelect';
import { StatusBadge } from '../../components/UI/StatusBadge';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import AccidentDetailModal, {
  accidentBadgeStatusFor,
  claimBadgeStatusFor,
} from '../../components/asset/AccidentDetailModal';
import ReportAccidentModal from '../../components/asset/ReportAccidentModal';
import ChangeAccidentDriverModal from '../../components/asset/ChangeAccidentDriverModal';
import { formatDateTime } from '../../utils/dateUtils';
import {
  useVehicleAccidents,
  useAccidentSummary,
} from '../../hooks/useAssetInventoryData';
import { useAssetDetail } from '../../contexts/AssetDetailContext';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'reported', label: 'Reported' },
  { value: 'pending_documents', label: 'Pending Documents' },
  { value: 'in_claim', label: 'In Claim' },
  { value: 'accident_closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const BASIS_FILTER_OPTIONS = [
  { value: '', label: 'All reporting bases' },
  { value: 'reporting_only', label: 'Reporting Only' },
  { value: 'third_party_claim', label: 'Third Party Claim' },
  { value: 'own_damage_claim', label: 'Own Damage Claim' },
  { value: 'no_reporting', label: 'No Reporting' },
];

const CLAIM_FILTER_OPTIONS = [
  { value: '', label: 'All claim statuses' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_assessment', label: 'Under Assessment' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'settled', label: 'Settled' },
  { value: 'private_settled', label: 'Privately Settled' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const PageSkeleton = () => (
  <div className="space-y-6 bg-gray-50 min-h-screen animate-pulse">
    <div className="bg-white rounded-2xl shadow-lg h-20" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((i) => <div key={i} className="bg-white rounded-2xl border h-24" />)}
    </div>
    <div className="bg-white rounded-2xl border h-96" />
  </div>
);

const InsurerDeadlineChip = ({ accident }) => {
  if (accident.is_legacy_import) {
    return <span className="text-xs text-gray-400">Imported</span>;
  }
  if (accident.insurer_notified_at) {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset bg-green-100 text-green-700 ring-green-600/20 whitespace-nowrap">
        Notified
      </span>
    );
  }
  if (!accident.insurer_report_deadline) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const hours = (new Date(accident.insurer_report_deadline) - new Date()) / 3600000;
  const tone = hours < 0
    ? 'bg-red-100 text-red-700 ring-red-600/20'
    : hours <= 12
      ? 'bg-amber-100 text-amber-700 ring-amber-600/20'
      : 'bg-gray-100 text-gray-600 ring-gray-500/20';
  const text = hours < 0
    ? `${Math.abs(Math.round(hours))}h overdue`
    : hours < 1
      ? `${Math.max(1, Math.round(hours * 60))}m left`
      : `${Math.round(hours)}h left`;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${tone}`}
      title={`Notify by ${formatDateTime(accident.insurer_report_deadline)}`}
    >
      Insurer: {text}
    </span>
  );
};

const nextActionFor = (accident) => {
  switch (accident.status) {
    case 'reported':
      return accident.insurer_notified_at
        ? 'Set reporting basis'
        : 'Notify insurer';
    case 'pending_documents':
      return accident.has_sas ? 'Complete documents / claim' : 'Awaiting SAS';
    case 'in_claim':
      return 'Track claim with insurer';
    default:
      return '';
  }
};

const AssetAdminFleetAccidents = () => {
  const [scope, setScope] = useState('open');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [basisFilter, setBasisFilter] = useState('');
  const [claimFilter, setClaimFilter] = useState('');
  const [detailAccident, setDetailAccident] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [changingDriver, setChangingDriver] = useState(null);

  const params = {
    ...(scope === 'all' ? { include_closed: 'true' } : {}),
    ...(search ? { search } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(basisFilter ? { reporting_basis: basisFilter } : {}),
    ...(claimFilter ? { claim_status: claimFilter } : {}),
  };
  const { data: accidents = [], isLoading, error } = useVehicleAccidents(params);
  const { data: summary } = useAccidentSummary();
  const { openAssetDetail } = useAssetDetail();

  if (isLoading) return <PageSkeleton />;

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load vehicle accidents</p>
          <p className="text-red-600 text-sm mt-1">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Siren className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                Vehicle Accidents
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
                Insurer notification, SAS &amp; claims — ordered by deadline
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setReporting(true)}
            className="btn btn-admin-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Report Accident
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Insurer Overdue"
          value={summary?.insurer_overdue ?? 0}
          subtitle="24h clock missed"
          icon={AlertTriangle}
          color="red"
        />
        <MetricCard
          title="Due Today"
          value={summary?.due_today ?? 0}
          subtitle="Notify insurer today"
          icon={CalendarClock}
          color="amber"
        />
        <MetricCard
          title="Awaiting SAS"
          value={summary?.awaiting_sas ?? 0}
          subtitle="Singapore Accident Statement"
          icon={FileText}
          color="blue"
        />
        <MetricCard
          title="Open Claims"
          value={summary?.open_claims ?? 0}
          subtitle="In claim with insurer"
          icon={CircleDollarSign}
          color="purple"
        />
      </div>

      {/* Queue */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Inbox className="w-4 h-4 text-gray-400" />
              {scope === 'open' ? 'Open Accidents' : 'All Accidents'} ({accidents.length})
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <DebouncedSearchInput
                value={search}
                onSearch={setSearch}
                placeholder="Search reference or plate..."
                theme="admin"
              />
              <PillToggle
                value={scope}
                onChange={setScope}
                options={[
                  { id: 'open', label: 'Open' },
                  { id: 'all', label: 'All' },
                ]}
                theme="admin"
                layout="sectionHeader"
                ariaLabel="Accident scope"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <FormSelect
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              theme="admin"
            />
            <FormSelect
              options={BASIS_FILTER_OPTIONS}
              value={basisFilter}
              onChange={setBasisFilter}
              theme="admin"
            />
            <FormSelect
              options={CLAIM_FILTER_OPTIONS}
              value={claimFilter}
              onChange={setClaimFilter}
              theme="admin"
            />
          </div>
        </div>

        {accidents.length === 0 ? (
          <div className="text-center py-12">
            <Siren className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {search || statusFilter || basisFilter || claimFilter
                ? 'No matching accidents'
                : 'No open accidents'}
            </h3>
            <p className="text-sm text-gray-500">
              {search || statusFilter || basisFilter || claimFilter
                ? 'Try different filters or search terms.'
                : 'Every accident has been closed or cancelled.'}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <div className="space-y-3 md:hidden">
              {accidents.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setDetailAccident(a)}
                  className="w-full text-left p-4 rounded-xl border border-gray-100 bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {a.vehicle_number_raw}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {a.reference} · {a.accident_type_display}
                      </p>
                    </div>
                    <StatusBadge
                      status={accidentBadgeStatusFor(a.status)}
                      label={a.status_display}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <InsurerDeadlineChip accident={a} />
                    {a.claim_status && a.claim_status !== 'not_started' && (
                      <StatusBadge
                        status={claimBadgeStatusFor(a.claim_status)}
                        label={a.claim_status_display}
                      />
                    )}
                  </div>
                  {nextActionFor(a) && (
                    <p className="text-xs text-gray-500 mt-1.5">{nextActionFor(a)}</p>
                  )}
                </button>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Vehicle / Ref</th>
                    <th className="px-4 py-3">Accident</th>
                    <th className="px-4 py-3">Driver</th>
                    <th className="px-4 py-3">Insurer Clock</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Claim</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {accidents.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setDetailAccident(a)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 whitespace-nowrap">
                          {a.vehicle_number_raw}
                        </div>
                        <div className="text-xs text-gray-400">{a.reference}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{a.accident_type_display}</div>
                        <div className="text-xs text-gray-400">
                          {formatDateTime(a.accident_datetime)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{a.driver_display_name || '—'}</div>
                        <div className="text-xs text-gray-400">{nextActionFor(a)}</div>
                      </td>
                      <td className="px-4 py-3"><InsurerDeadlineChip accident={a} /></td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={accidentBadgeStatusFor(a.status)}
                          label={a.status_display}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {a.claim_status && a.claim_status !== 'not_started' ? (
                          <StatusBadge
                            status={claimBadgeStatusFor(a.claim_status)}
                            label={a.claim_status_display}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="w-4 h-4 text-gray-300 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <AccidentDetailModal
        isOpen={!!detailAccident}
        onClose={() => setDetailAccident(null)}
        accident={detailAccident}
        onViewVehicle={(assetId) => {
          setDetailAccident(null);
          openAssetDetail(assetId);
        }}
        onChangeDriver={(acc) => setChangingDriver(acc)}
      />

      <ReportAccidentModal
        isOpen={reporting}
        onClose={() => setReporting(false)}
        theme="admin"
      />

      <ChangeAccidentDriverModal
        isOpen={!!changingDriver}
        onClose={() => setChangingDriver(null)}
        accident={changingDriver}
      />
    </div>
  );
};

export default AssetAdminFleetAccidents;
